import { c as _c } from "react/compiler-runtime";
import { IcoArrowRightSmall16, IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import type { ApiNodeDataAssignData, DataIdentifier } from '@onefootprint/request-types/dashboard';
import { Form, IconButton, InlineAlert, LinkButton, type TemplateInputProps } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../base-node';
import { getDefaultValues } from '../utils';
type DataAssignmentNodeFormProps = {
  node: NodeProps<ApiNodeDataAssignData>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
};
type DataAssignmentFormData = {
  dataAssignments: {
    destination: DataIdentifier | '';
    source: string;
  }[];
};
export const DataAssignmentNodeForm = t0 => {
  const $ = _c(58);
  const {
    node,
    mutation,
    canEdit
  } = t0;
  const {
    toggle
  } = useDirty();
  const t1 = !canEdit;
  let t2;
  if ($[0] !== node.data.dataAssignments) {
    t2 = getDefaultValues(node.data.dataAssignments);
    $[0] = node.data.dataAssignments;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = {
      dataAssignments: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = {
      disabled: t1,
      defaultValues: t3
    };
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const form = useForm(t4);
  const config = useGetNodeConfigContext(node.id, "templatable");
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      keyPrefix: "dataAssignmentNode"
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    t
  } = useTranslation("playbook-builder", t5);
  let t6;
  if ($[8] !== form.control) {
    t6 = {
      control: form.control,
      name: "dataAssignments"
    };
    $[8] = form.control;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t6);
  let t7;
  if ($[10] !== config?.autocomplete) {
    t7 = Object.entries(config?.autocomplete || {}).reduce(_temp2, {} as TemplateInputProps['autocompleteItems']);
    $[10] = config?.autocomplete;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const autocompleteItems = t7;
  let t8;
  if ($[12] !== form.formState.isDirty || $[13] !== toggle) {
    t8 = () => {
      toggle("data-assignment-form", form.formState.isDirty);
    };
    $[12] = form.formState.isDirty;
    $[13] = toggle;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== form.formState.isDirty) {
    t9 = [form.formState.isDirty];
    $[15] = form.formState.isDirty;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  if ($[17] !== append) {
    t10 = () => {
      append({
        destination: "",
        source: ""
      }, {
        shouldFocus: false
      });
    };
    $[17] = append;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const onAddAssignment = t10;
  let t11;
  if ($[19] !== mutation || $[20] !== node.id) {
    t11 = async data => {
      ;
      try {
        await mutation.mutateAsync({
          kind: "update_node",
          nodeId: node.id,
          nodeData: {
            kind: "assign_data",
            dataAssignments: data.dataAssignments.map(_temp3)
          }
        });
      } catch (t12) {
        const error = t12;
        console.error("Error updating playbook session:", error);
      }
    };
    $[19] = mutation;
    $[20] = node.id;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleFormSubmit = t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <InlineAlert variant="neutral" className="mb-4"><Trans i18nKey={_temp4} components={{
        brackets: <span className="text-snippet-2">{"{{ }}"}</span>,
        firstName: <span className="text-snippet-2">{"{{ vault.id.first_name }}"}</span>
      }} /></InlineAlert>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== form || $[24] !== handleFormSubmit) {
    t13 = form.handleSubmit(handleFormSubmit);
    $[23] = form;
    $[24] = handleFormSubmit;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t) {
    t14 = t(_temp5);
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14) {
    t15 = <label htmlFor="destination" className="text-body-2">{t14}</label>;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== autocompleteItems || $[31] !== canEdit || $[32] !== fields || $[33] !== form.formState.errors || $[34] !== remove || $[35] !== t) {
    let t17;
    if ($[37] !== autocompleteItems || $[38] !== canEdit || $[39] !== fields.length || $[40] !== form.formState.errors || $[41] !== remove || $[42] !== t) {
      t17 = (field, index) => {
        const hasError = form.formState.errors.dataAssignments?.[index];
        return <div key={field.id} className={cx("grid grid-cols-[auto_minmax(0,1fr)_auto] gap-3 items-center md:flex md:gap-4", {
          "pb-4 max-md:gap-y-7": hasError
        })}><div className="col-span-full md:contents"><Form.TemplateInput name={`dataAssignments.${index}.source`} autocompleteItems={autocompleteItems} placeholder={t(_temp6)} size="compact" autoFocus={true} rules={{
              required: t(_temp7)
            }} errorPosition="absolute" /></div><IcoArrowRightSmall16 className="flex-shrink-0" /><Form.Input name={`dataAssignments.${index}.destination`} placeholder={t(_temp8)} addon="vault." size="compact" rules={{
            required: t(_temp9)
          }} errorPosition="absolute" /><IconButton type="button" onClick={() => remove(index)} icon={IcoTrash16} destructive={true} disabled={fields.length === 1 || !canEdit} className="flex-shrink-0" aria-label="Remove assignment" /></div>;
      };
      $[37] = autocompleteItems;
      $[38] = canEdit;
      $[39] = fields.length;
      $[40] = form.formState.errors;
      $[41] = remove;
      $[42] = t;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    t16 = fields.map(t17);
    $[30] = autocompleteItems;
    $[31] = canEdit;
    $[32] = fields;
    $[33] = form.formState.errors;
    $[34] = remove;
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = !canEdit;
  let t18;
  if ($[44] !== t) {
    t18 = t(_temp10);
    $[44] = t;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== onAddAssignment || $[47] !== t17 || $[48] !== t18) {
    t19 = <LinkButton type="button" onClick={onAddAssignment} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={t17}>{t18}</LinkButton>;
    $[46] = onAddAssignment;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t13 || $[51] !== t15 || $[52] !== t16 || $[53] !== t19) {
    t20 = <form id="data-assignment-form" onSubmit={t13} className="flex flex-col gap-5 border border-tertiary rounded p-4 md:gap-4">{t15}{t16}{t19}</form>;
    $[50] = t13;
    $[51] = t15;
    $[52] = t16;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== form || $[56] !== t20) {
    t21 = <FormProvider {...form}>{t12}{t20}</FormProvider>;
    $[55] = form;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  return t21;
};
function _temp(item) {
  return {
    label: item.display,
    value: item.token,
    detail: item.dataType.join(" | ")
  };
}
function _temp2(acc, t0) {
  const [key, value] = t0;
  acc[key === "workflowContextKeys" ? "others" : key] = value.map(_temp);
  return acc;
}
function _temp3(assignment) {
  return {
    ...assignment,
    destination: {
      vault: assignment.destination as DataIdentifier
    }
  };
}
function _temp4($) {
  return $.playbookBuilderTemplating.tooltip;
}
function _temp5($_0) {
  return $_0.label;
}
function _temp6($_1) {
  return $_1.source.placeholder;
}
function _temp7($_2) {
  return $_2.source.required;
}
function _temp8($_3) {
  return $_3.destination.placeholder;
}
function _temp9($_4) {
  return $_4.destination.required;
}
function _temp10($_5) {
  return $_5.addNew;
}